<script>
import { mapActions, mapState } from 'vuex';
import Format from './format';

export default {
  name: 'Edit',
  mixins: [
    Format,
  ],
  data() {
    return {
      sub: this.subchannels[0],
    };
  },
  props: {
    schedule: {
      type: Object,
      required: true,
    },
    snackbar: {
      type: Object,
      required: true,
    },
    subchannels: Array,
  },
  computed: {
    ...mapState({
      patched: state => state.schedules.patchSchedule,
      postSchedule: state => state.schedules.postSchedule,
    }),
    showNewJobStatus() {
      return this.schedule.weekdays && this.schedule.minutes && this.schedule.hours;
    },
  },
  methods: {
    ...mapActions({
      postSchedules: 'schedules/postSchedules',
      patchSchedule: 'schedules/patchSchedule',
      updateSchedules: 'schedules/updateSchedules',
      getSchedules: 'schedules/getSchedules',
    }),
    setSnackBar(color, text) {
      this.$emit('update:snackbar',
        {
          ...this.snackbar,
          color,
          timeout: 4000,
          text,
          snackbar: true,
        });
    },
    patch() {
      const checkForLettersAndSymbols = /[a-zA-Z!@#$%^&()_+\-=[\]{};':"\\|.<>/?]+/g;
      if (this.schedule.minutes === '' || this.schedule.hours === '') {
        this.setSnackBar('error', 'Inputs cannot be empty');
      } else if (
        this.schedule.minutes.match(checkForLettersAndSymbols) ||
        this.schedule.hours.match(checkForLettersAndSymbols) ||
        Format.checkMinuteHourValues(this.schedule.minutes, this.schedule.hours)
      ) {
        this.setSnackBar('error', 'Minute and hour input cannot contain letters or symbols other than "," & "*"' +
          'and must be on format: hours:0-23 & minutes: 0-59');
      } else {
        const requestOptions = {
          webservice: this.$route.params.webservice,
          subName: this.schedule.sub,
          cronArray: this.fixCommasAndSpaces(
            this.schedule.minutes, this.schedule.hours,
          ),
          weekdays: this.sortWeekdays(this.schedule.weekdays).join(','),
          id: this.schedule.scheduleId,
        };
        this.patchSchedule(requestOptions).then(() => {
        }).then(() => {
          if (this.patched.items === 204) {
            this.setSnackBar('success', 'Schedule successfully updated');
            this.clearValuesOnEditStop();
          } else {
            this.setSnackBar('error', `${this.patched.items}`);
          }
          this.updateSchedules(this.$route.params.webservice);
          this.getSchedules();
        });
      }
    },
    post(sub, minutes, hours, weekdays) {
      const checkForLettersAndSymbols = /[a-zA-Z!@#$%^&()_+\-=[\]{};':"\\|.<>/?]+/g;
      if (this.schedule.minutes === '' || this.schedule.hours === '') {
        this.setSnackBar('error', 'inputs cannot be empty');
      } else if (
        this.schedule.minutes.match(checkForLettersAndSymbols) ||
        this.schedule.hours.match(checkForLettersAndSymbols) ||
        Format.checkMinuteHourValues(this.schedule.minutes, this.schedule.hours)
      ) {
        this.setSnackBar('error', 'Minute and hour input cannot contain letters or symbols other than "," & "*"' +
          ' and must be on format: hours:0-23 & minutes: 0-59');
      } else {
        const requestOptions = {
          webservice: this.$route.params.webservice,
          subName: this.sub,
          cronArray: this.fixCommasAndSpaces(minutes, hours),
          weekdays: this.sortWeekdays(weekdays).join(','),
        };
        this.postSchedules(requestOptions).then(() => {
          if (this.postSchedule.items === '204') {
            this.setSnackBar('success', 'Schedule successfully created');
            this.clearValuesOnEditStop();
          } else {
            this.setSnackBar('error', `${this.postSchedule.items}`);
          }
        }).then(() => {
          this.updateSchedules(this.$route.params.webservice);
          this.getSchedules();
        });
      }
    },
    clearValuesOnEditStop() {
      this.$nextTick(() => {
        this.$emit('update:schedule', {
          ...this.schedule,
          weekdays: this.schedule.values,
          minutes: null,
          hours: null,
          id: null,
          isEdit: false,
          isSubmit: true,
        });
      });
    },
  },
};
</script>

<template>
  <v-form ref="input">
    <v-layout row wrap>
      <v-flex v-if="subchannels.length !== 0 && schedule.isSubmit" xs12 md4>
        <v-select
          v-model="sub"
          :items="subchannels"
          label="Subchannel"
          id="subChannelPost"
          class="subInput"
        />
      </v-flex>
      <v-flex v-if="schedule.subs.length !== 0 && schedule.isEdit" xs12 md4>
        <v-select
          :value="schedule.sub"
          :items="schedule.subs"
          label="Subchannel"
          id="subChannelEdit"
        />
      </v-flex>
    </v-layout>
    <v-layout row wrap>
      <v-flex class="justify-start xs12 md7">
        <p>
          Minutes and hours should be input by comma separated values
          e.g.: 01,31,... or by singe values.
        </p>
        <p>
          "*" may be used to cover
          every single value, e.g.: hours: * = hours 1-24
        </p>
      </v-flex>
    </v-layout>
    <v-layout row wrap>
      <v-flex
        :key="weekday"
        v-for="weekday in schedule.values"
        class="d-inline-block"
        xs1
        md1
      >
        <v-checkbox
          :key="weekday"
          :value="weekday"
          :input-value="schedule.weekdays"
          :label="weekday"
          @change="$emit('update:schedule', { ...schedule, weekdays: $event })"
        >
        </v-checkbox>
      </v-flex>
    </v-layout>
    <v-layout class="row wrap align-center">
      <v-flex class="xs1 md1 align-center">
        <v-label for="hourInput">
          Hours:
        </v-label>
      </v-flex>
      <v-flex xs5 md3>
        <v-text-field
          :rules="[noLettersOrSymbols, noDuplicates]"
          :value="schedule.hours"
          class="text-field-height-fix"
          id="hourInput"
          @input="$emit('update:schedule', { ...schedule, hours: $event })"
        />
      </v-flex>
    </v-layout>
    <v-layout class="row wrap align-center">
      <v-flex xs1 md1>
        <v-label for="minuteInput">
          Minutes:
        </v-label>
      </v-flex>
      <v-flex xs5 md3>
        <v-text-field
          :rules="[noLettersOrSymbols, noDuplicates]"
          :value="schedule.minutes"
          class="text-field-height-fix"
          id="minuteInput"
          @input="$emit('update:schedule', { ...schedule, minutes: $event })"
        />
      </v-flex>
      <br>
    </v-layout>
    <v-layout class="row wrap align-center">
      <v-flex xs12 md5>
        <p>
          <template v-if="showNewJobStatus">
            Job will run at minutes: {{ schedule.minutes }} on hours:
            {{ schedule.hours }} : {{ schedule.weekdays.join(", ") }}
          </template>
        </p>
      </v-flex>
      <v-flex v-if="schedule.isEdit" xs12 md1>
        <v-btn
          label="stopEdit"
          color="error"
          @click="clearValuesOnEditStop"
        >
          Stop edit
        </v-btn>
      </v-flex>
      <v-flex v-if="schedule.isEdit" xs12 md1>
        <v-btn
          label="Edit"
          color="success"
          @click="patch"
        >
          Update
        </v-btn>
      </v-flex>
      <v-flex v-if="schedule.isSubmit" xs2 offset-md1 md1>
        <v-btn
          label="Submit"
          color="success"
          @click="post(schedule.sub, schedule.minutes, schedule.hours, schedule.weekdays)"
        >
          Submit
        </v-btn>
      </v-flex>
    </v-layout>
  </v-form>
</template>



// WEBPACK FOOTER //
// src/components/schedules/Edit.vue